'use client';

import { useState } from 'react';

import { CtfRichText } from '@/modules/common/components/CtfRichText';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/modules/common/components/ui/Accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/modules/common/components/ui/tabs';
import { sendGTMEvent } from '@/modules/common/services/sendGTMEvent';
import { useFilteredCollectionProducts } from '@/modules/product/hooks/useFilteredCollectionProducts';
import { useSkuSearch } from '@/modules/product/hooks/useSkuSearch';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { SampleCardSkeleton } from '@/modules/sample-card/components/shared/SampleCardSkeleton';

import { AlgoliaProvider } from '../AlgoliaSearch/AlgoliaProvider';
import { CollectionListGridDesigner } from './CollectionListGridDesigner';

type CollectionContentDesignerProps = {
  collection: ContentfulCollection;
  productSkus: string[];
};

const CollectionContentDesigner = ({ collection, productSkus }: CollectionContentDesignerProps) => {
  const { validProducts: products, isPending } = useSkuSearch(productSkus);
  const [activeTab, setActiveTab] = useState('designPicks');
  const filteredCollectionProducts = useFilteredCollectionProducts(collection.products);

  const handleTabClick = (tabName: string) => {
    // Only send event if tab is changing
    if (tabName !== activeTab) {
      setActiveTab(tabName);

      // Get readable tab name
      const tabDisplayName =
        tabName === 'designPicks' ? collection.designPicksTitle
        : tabName === 'designTips' ? collection.designTipsTitle
        : tabName;

      // Send dataLayer event
      if (typeof window !== 'undefined' && window.dataLayer) {
        sendGTMEvent({
          event: 'tabClicked',
          eventData: {
            feature: 'Designer collection',
            where: 'Feature content',
            name: tabDisplayName,
          },
        });
      }
    }
  };

  const tips = collection?.designTipsListCollection?.items;
  const filteredTips = tips?.filter((tip): tip is NonNullable<typeof tip> => !!tip?.title) ?? [];

  return (
    <div className="pt-6 lg:pt-12">
      <Tabs defaultValue="designPicks" value={activeTab} onValueChange={handleTabClick}>
        <TabsList className="px-4 pb-5 lg:px-0">
          <TabsTrigger value="designTips" className="lg:h-auto">
            {collection.designTipsTitle}
          </TabsTrigger>
          <TabsTrigger value="designPicks" className="lg:h-auto">
            {collection.designPicksTitle}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="designTips" className="mt-0" forceMount>
          <div className="space-y-4 px-4 lg:px-0">
            <Accordion
              type="multiple"
              defaultValue={[filteredTips?.[0]?.title ?? '']}
              onValueChange={(value) => {
                if (!value) {
                  sendGTMEvent({
                    event: 'sectionCollapsed',
                    eventData: {
                      feature: 'Designer collection',
                      where: 'Feature content',
                      type: 'Design tips',
                      name: value,
                    },
                  });
                } else {
                  sendGTMEvent({
                    event: 'sectionExpanded',
                    eventData: {
                      feature: 'Designer collection',
                      where: 'Feature content',
                      type: 'Design tips',
                      name: value,
                    },
                  });
                }
              }}
            >
              {filteredTips?.map(({ title, content }, index) => (
                <AccordionItem
                  key={title ?? index}
                  value={title ?? ''}
                  className="mb-3 !border-b-0"
                >
                  <AccordionTrigger>
                    <span className="text-left text-[18px] font-bold leading-[24px]">{title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="md:pb-0">
                    <CtfRichText
                      className="py-0 text-[18px] leading-[24px] lg:py-0"
                      json={content?.json}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </TabsContent>
        <TabsContent value="designPicks" forceMount>
          {isPending || products.length === 0 ?
            <CollectionDesignerSkeleton />
          : <AlgoliaProvider>
              <CollectionListGridDesigner
                products={products}
                collection={collection}
                exactMatches={filteredCollectionProducts
                  ?.filter((product) => product.exactMatch)
                  ?.map((product) => product.sku)}
              />
            </AlgoliaProvider>
          }
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CollectionDesignerSkeleton = () => {
  return (
    <div className="grid h-fit grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <SampleCardSkeleton key={i} />
      ))}
    </div>
  );
};

export default CollectionContentDesigner;
